// Global Instructions Rule Applied!
//
// Tranche full-form payload (view__tranche_detail): tranche facts + current
// position, and the chronological transaction history with post-transaction
// point-in-time measures. Strict like the other deal-execution models — a
// new RPC key must be added here or the fetch fails loudly.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

export const TrancheDetailSchema = z
  .object({
    tranches__id: z.string(),
    tranches__name: z.string().nullish(),
    tranches__warehoused: z.boolean().nullish(),
    tranches__secondary_purchase: z.boolean().nullish(),
    funds__id: z.string().nullish(),
    funds__name: z.string().nullish(),
    accounts__id: z.string().nullish(),
    accounts__name: z.string().nullish(),
    securities__id: z.string().nullish(),
    securities__name: z.string().nullish(),
    cfg_security_types__label: z.string().nullish(),
    cfg_asset_valuation_types__label: z.string().nullish(),
    currencies__symbol: z.string().nullish(),
    currencies__code: z.string().nullish(),
    parent_tranches__id: z.string().nullish(),
    parent_tranches__name: z.string().nullish(),
    up_tranches__id: z.string().nullish(),
    up_tranches__name: z.string().nullish(),
    asset_transactions__id: z.string().nullish(),
    asset_transactions__name: z.string().nullish(),
    prog__date_invested: z.string().nullish(),
    prog__date_exited: z.string().nullish(),
    prog__no_held: z.number().nullish(),
    prog__units_held: z.number().nullish(),
    prog__cost_held: z.number().nullish(),
    prog__cost_ps: z.number().nullish(),
    prog__fmv: z.number().nullish(),
    prog__unit_price: z.number().nullish(),
    prog__proceeds: z.number().nullish(),
    prog__escrow_balance: z.number().nullish(),
    prog__perc_exited: z.number().nullish(),
    // the interest the tranche has accrued under its security's terms at the
    // close of today, in the security currency (at__interest_accrued: the
    // calculation, not a booking); null = the security carries no terms
    prog__interest_accrued: z.number().nullish(),
    prog__interest_capitalised: z.number().nullish(),
    // the score's four interest measures (functional; PR B)
    prog__interest_balance: z.number().nullish(),
    prog__interest_earned: z.number().nullish(),
    prog__interest_received: z.number().nullish(),
    prog__cost_capitalized: z.number().nullish(),
    // the two non-cash projections (asset-delta design, 2026-10-21): basis
    // that arrived without cash, gain or loss recognised without proceeds
    prog__cost_adjusted: z.number().nullish(),
    prog__gain_recognized: z.number().nullish()
  })
  .strict()

export type TrancheDetail = z.infer<typeof TrancheDetailSchema>

export const TrancheHistoryEntrySchema = z
  .object({
    asset_transactions__id: z.string(),
    asset_transactions__name: z.string().nullish(),
    asset_transactions__transaction_date: z.string().nullish(),
    asset_transaction_types__label: z.string().nullish(),
    transaction_statuses__label: z.string().nullish(),
    funds__name: z.string().nullish(),
    prog__cost_held: z.number().nullish(),
    prog__no_held: z.number().nullish(),
    prog__fmv: z.number().nullish(),
    prog__unit_price: z.number().nullish(),
    prog__unrealized: z.number().nullish(),
    prog__no_disposed: z.number().nullish(),
    prog__proceeds: z.number().nullish(),
    prog__realized: z.number().nullish(),
    prog__escrow_balance: z.number().nullish()
  })
  .strict()

export type TrancheHistoryEntry = z.infer<typeof TrancheHistoryEntrySchema>

export const TrancheDetailPayloadSchema = z
  .object({
    tranche: TrancheDetailSchema,
    history: z.array(TrancheHistoryEntrySchema)
  })
  .strict()

export type TrancheDetailPayload = z.infer<typeof TrancheDetailPayloadSchema>

export function validateTrancheDetailPayload(data: unknown): TrancheDetailPayload {
  try {
    return validateWithZodSchema(
      TrancheDetailPayloadSchema,
      data,
      'tranche detail'
    ) as TrancheDetailPayload
  } catch (e) {
    throw new Error(`Tranche detail validation failed: ${e}`)
  }
}
